import {
  Add,
  Delete,
  Edit,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  createCashflow,
  deleteCashflow,
  getBrokers,
  getCashflows,
  getCashflowsSummary,
  updateCashflow,
} from "../../../services/portfolioService";
import { useCurrency } from "../../Auth/AuthContext";

const CashFlowsPage = () => {
  const [cashFlows, setCashFlows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [brokers, setBrokers] = useState([]);

  const {
    selectedCurrency,
    convert,
    formatMoney,
    ratesReady,
    userCurrency,
    convertAndFormat,
  } = useCurrency();

  const [formData, setFormData] = useState({
    broker_id: "",
    flow_type: "DEPOSIT",
    amount: "",
    currency: "USD",
    flow_date: new Date().toISOString().split("T")[0],
    description: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [flowsRes, summaryRes, brokersRes] = await Promise.all([
        getCashflows(),
        getCashflowsSummary(),
        getBrokers(),
      ]);
      setCashFlows(flowsRes);
      setSummary(summaryRes);
      setBrokers(brokersRes);
    } catch (err) {
      console.error("Error fetching cash flows:", err);
      setError("Failed to load cash flows");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (flow = null) => {
    if (flow) {
      setEditMode(true);
      setCurrentFlow(flow);
      setFormData({
        broker_id: flow.broker_id || "",
        flow_type: flow.flow_type,
        amount: flow.amount,
        currency: flow.currency || "USD",
        flow_date: flow.flow_date,
        description: flow.description || "",
      });
    } else {
      setEditMode(false);
      setCurrentFlow(null);
      setFormData({
        broker_id: "",
        flow_type: "DEPOSIT",
        amount: "",
        currency: "USD",
        flow_date: new Date().toISOString().split("T")[0],
        description: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setCurrentFlow(null);
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await updateCashflow(currentFlow.id, formData);
      } else {
        await createCashflow(formData);
      }

      handleCloseDialog();
      fetchData();
    } catch (err) {
      console.error("Error saving cash flow:", err);
      setError(err.response?.data?.message || "Failed to save cash flow");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this cash flow?"))
      return;

    try {
      await deleteCashflow(id);
      fetchData();
    } catch (err) {
      console.error("Error deleting cash flow:", err);
      setError("Failed to delete cash flow");
    }
  };

  const getFlowTypeColor = (type) => {
    switch (type) {
      case "DEPOSIT":
        return "success";
      case "WITHDRAWAL":
        return "error";
      case "DIVIDEND_CASH":
        return "info";
      case "INTEREST":
        return "primary";
      case "FEE":
        return "warning";
      default:
        return "default";
    }
  };

  const getFlowTypeIcon = (type) => {
    if (["DEPOSIT", "DIVIDEND_CASH", "INTEREST"].includes(type)) {
      return <TrendingUp fontSize="small" />;
    }
    return <TrendingDown fontSize="small" />;
  };

  const getConvertedSummary = () => {
    if (!cashFlows) {
      return (
        cashFlows?.overall || {
          total_deployed: 0,
          total_deposits: 0,
          total_dividends: 0,
          total_fees: 0,
          total_interest: 0,
          total_transactions: 0,
          total_withdrawals: 0,
        }
      );
    }

    let totalDeposits = 0;
    let totalWithdrawals = 0;
    let totalDividends = 0;
    let totalInterest = 0;
    let totalFees = 0;

    let totalTransactions = 0;

    var currencyUsed;
    if (selectedCurrency === "DEFAULT") {
      currencyUsed = userCurrency;
    } else {
      currencyUsed = selectedCurrency;
    }

    cashFlows?.forEach((pos) => {
      if (pos.flow_type === "DEPOSIT") {
        totalDeposits += convert(pos.amount, pos.currency, currencyUsed);
      }
      if (pos.flow_type === "WITHDRAWAL") {
        totalWithdrawals += convert(pos.amount, pos.currency, currencyUsed);
      }
      if (pos.flow_type === "DIVIDEND_CASH") {
        totalDividends += convert(pos.amount, pos.currency, currencyUsed);
      }
      if (pos.flow_type === "INTEREST") {
        totalInterest += convert(pos.amount, pos.currency, currencyUsed);
      }
      if (pos.flow_type === "FEE") {
        totalFees += convert(pos.amount, pos.currency, currencyUsed);
      }
      totalTransactions++;
    });

    return {
      total_deployed:
        totalDeposits +
        totalDividends +
        totalInterest -
        totalWithdrawals -
        totalFees,
      total_deposits: totalDeposits,
      total_dividends: totalDividends,
      total_fees: totalFees,
      total_interest: totalInterest,
      total_transactions: totalTransactions,
      total_withdrawals: totalWithdrawals,
      currency: currencyUsed,
    };
  };

  const convertedSummary = getConvertedSummary();

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={600}>
          Cash Flows
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Cash Flow
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {convertedSummary && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              <CardContent>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  Total Deployed Capital
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight={600}
                  sx={{ color: "white", mt: 1 }}
                >
                  {convertAndFormat(
                    convertedSummary.total_deployed,
                    convertedSummary.currency,
                  )}
                  {/* {formatMoney(
                    Math.abs(summary.total_deployed),
                    selectedCurrency === "DEFAULT"
                      ? userCurrency
                      : selectedCurrency,
                    selectedCurrency,
                  )} */}
                  {/* {selectedCurrency === "DEFAULT"
                    ? formatMoney(
                        Math.abs(convertedSummary.total_deployed),
                        convertedSummary.currency,
                        convertedSummary.currency,
                      )
                    : formatMoney(
                        convert(
                          Math.abs(convertedSummary.total_deployed),
                          convertedSummary.currency,
                          selectedCurrency,
                        ),
                        selectedCurrency,
                        selectedCurrency,
                      )} */}
                  {/* {parseFloat(summary.total_deployed || 0).toLocaleString(
                    "en-US",
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                  )} */}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    display: "block",
                    mt: 1,
                  }}
                >
                  Net: Deposits + Dividends + Interest - Withdrawals - Fees
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Total Deposits
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="success.main"
                  mt={1}
                >
                  +
                  {/* {selectedCurrency === "DEFAULT"
                    ? formatMoney(
                        convertedSummary.total_deposits,
                        convertedSummary.currency,
                        convertedSummary.currency,
                      )
                    : formatMoney(
                        convert(
                          convertedSummary.total_deposits,
                          convertedSummary.currency,
                          selectedCurrency,
                        ),
                        selectedCurrency,
                        selectedCurrency,
                      )} */}
                  {convertAndFormat(
                    convertedSummary.total_deposits,
                    convertedSummary.currency,
                  )}
                </Typography>
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Dividends:{" "}
                  </Typography>
                  <Typography variant="body2" component="span" fontWeight={600}>
                    {convertAndFormat(
                      convertedSummary.total_dividends,
                      convertedSummary.currency,
                    )}
                    {/* {selectedCurrency === "DEFAULT"
                      ? formatMoney(
                          convertedSummary.total_dividends,
                          convertedSummary.currency,
                          convertedSummary.currency,
                        )
                      : formatMoney(
                          convert(
                            convertedSummary.total_dividends,
                            convertedSummary.currency,
                            selectedCurrency,
                          ),
                          selectedCurrency,
                          selectedCurrency,
                        )} */}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Interest:{" "}
                  </Typography>
                  <Typography variant="body2" component="span" fontWeight={600}>
                    {/* {selectedCurrency === "DEFAULT"
                      ? formatMoney(
                          convertedSummary.total_interest,
                          convertedSummary.currency,
                          convertedSummary.currency,
                        )
                      : formatMoney(
                          convert(
                            convertedSummary.total_interest,
                            convertedSummary.currency,
                            selectedCurrency,
                          ),
                          selectedCurrency,
                          selectedCurrency,
                        )} */}
                    {convertAndFormat(
                      convertedSummary.total_interest,
                      convertedSummary.currency,
                    )}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Total Withdrawals
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="error.main"
                  mt={1}
                >
                  -
                  {convertAndFormat(
                    convertedSummary.total_withdrawals,
                    convertedSummary.currency,
                  )}
                  {/* {selectedCurrency === "DEFAULT"
                    ? formatMoney(
                        convertedSummary.total_withdrawals,
                        convertedSummary.currency,
                        convertedSummary.currency,
                      )
                    : formatMoney(
                        convert(
                          convertedSummary.total_withdrawals,
                          convertedSummary.currency,
                          selectedCurrency,
                        ),
                        selectedCurrency,
                        selectedCurrency,
                      )} */}
                </Typography>
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Fees:{" "}
                  </Typography>
                  <Typography variant="body2" component="span" fontWeight={600}>
                    {convertAndFormat(
                      convertedSummary.total_fees,
                      convertedSummary.currency,
                    )}
                    {/* {selectedCurrency === "DEFAULT"
                      ? formatMoney(
                          convertedSummary.total_fees,
                          convertedSummary.currency,
                          convertedSummary.currency,
                        )
                      : formatMoney(
                          convert(
                            convertedSummary.total_fees,
                            convertedSummary.currency,
                            selectedCurrency,
                          ),
                          selectedCurrency,
                          selectedCurrency,
                        )} */}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Cash Flows Table */}
      <Paper>
        <Box p={2} borderBottom={1} borderColor="divider">
          <Typography variant="h6" fontWeight={600}>
            Transaction History
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Broker</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cashFlows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={4}>
                      No cash flows recorded
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                cashFlows.map((flow) => (
                  <TableRow key={flow.id}>
                    <TableCell>
                      {new Date(flow.flow_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={flow.flow_type.replace("_", " ")}
                        size="small"
                        color={getFlowTypeColor(flow.flow_type)}
                        icon={getFlowTypeIcon(flow.flow_type)}
                      />
                    </TableCell>
                    <TableCell>{flow.broker_name || "External"}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color: [
                            "DEPOSIT",
                            "DIVIDEND_CASH",
                            "INTEREST",
                          ].includes(flow.flow_type)
                            ? "success.main"
                            : "error.main",
                        }}
                      >
                        {["DEPOSIT", "DIVIDEND_CASH", "INTEREST"].includes(
                          flow.flow_type,
                        )
                          ? "+"
                          : "-"}
                        {/* {formatMoney(flow.amount, flow.currency, flow.currency)} */}
                        {convertAndFormat(flow.amount, flow.currency)}
                        {/* {formatMoney(
                          flow.amount,
                          selectedCurrency === "DEFAULT"
                            ? userCurrency
                            : selectedCurrency,
                          selectedCurrency,
                        )} */}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {flow.description || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(flow)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(flow.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "Edit Cash Flow" : "Add Cash Flow"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField
              select
              label="Flow Type *"
              value={formData.flow_type}
              onChange={(e) =>
                setFormData({ ...formData, flow_type: e.target.value })
              }
              required
            >
              <MenuItem value="DEPOSIT">Deposit</MenuItem>
              <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
              <MenuItem value="DIVIDEND_CASH">Dividend (Cash)</MenuItem>
              <MenuItem value="INTEREST">Interest</MenuItem>
              <MenuItem value="FEE">Fee</MenuItem>
              <MenuItem value="TRANSFER_IN">Transfer In</MenuItem>
              <MenuItem value="TRANSFER_OUT">Transfer Out</MenuItem>
            </TextField>

            <TextField
              select
              label="Broker"
              value={formData.broker_id}
              onChange={(e) =>
                setFormData({ ...formData, broker_id: e.target.value })
              }
            >
              <MenuItem value="">External</MenuItem>
              {brokers.map((broker) => (
                <MenuItem key={broker.id} value={broker.id}>
                  {broker.name}
                </MenuItem>
              ))}
            </TextField>

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                label="Amount *"
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                required
              />
              <TextField
                select
                label="Currency"
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="RON">RON</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
              </TextField>
            </Box>

            <TextField
              label="Date *"
              type="date"
              value={formData.flow_date}
              onChange={(e) =>
                setFormData({ ...formData, flow_date: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="E.g., Salary deposit, Withdrawn for vacation, Bond coupon payment"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CashFlowsPage;
